import classNames from "classnames";
import css from "./CardStack.module.scss";
import { Surface, SurfaceTone } from "./Surface";

interface CardStackProps {
    /** Each direct child is rendered as one stacked row, separated by a divider. */
    readonly children: React.ReactNode;
    /**
     * Applied to the container for height behavior — e.g. a class that sets
     * `max-height` + `overflow-y: auto` to scroll, or `flex: 1; min-height: 0` to
     * fill available space.
     */
    readonly className?: string;
    /** Background fill. Defaults to transparent. */
    readonly tone?: SurfaceTone;
    /**
     * Give every child the container's column tracks via `subgrid`, so their
     * cells align across rows. Declare the tracks themselves (and any gap) on
     * `className` — this only wires the children up to them.
     */
    readonly columns?: boolean;
}

/**
 * A generic bordered, rounded container that stacks its children vertically with
 * a divider between each. Not tied to any particular content.
 */
export const CardStack: React.FC<CardStackProps> = ({
    children,
    className,
    tone = "none",
    columns = false
}) => (
    <Surface
        tone={tone}
        bordered
        radius="md"
        className={classNames(
            css["CardStack"],
            { [css["CardStack--columns"]]: columns },
            className
        )}
    >
        {children}
    </Surface>
);
